import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Autocomplete, Box, Button, Snackbar, TextField, Typography } from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { GridLoader } from "react-spinners";
import {
  atualizarBaseLegalIcmsSt,
  buscarBasesLegais,
  buscarSumarizacaoIcmsStApuracao,
  exportarSumarizacaoApuracao,
} from "../../../services/apuracao.service";
import { listarProjetosDoCliente, salvarProjeto } from "../../../services/projetos-apuracao.service";
import Pagination from "../../../components/Pagination";
import BotaoExportacaoProgresso from "../../../components/BotaoExportacaoProgresso";
import useExportacaoAssincrona from "../Detalhe/hooks/useExportacaoAssincrona";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import SelectFiltro, { BuscaFiltro, FILTER_FIELD_SX, PopperAjustado } from "../components/SelectFiltro";
import BadgeStatus from "../components/BadgeStatus";
import ModalReprocessarAba from "../components/ModalReprocessarAba";
import ModalDetalhesCruzamentoIcmsSt from "./ModalDetalhesCruzamentoIcmsSt";
import ModalSalvarProjeto from "../../ProjetosApuracao/components/ModalSalvarProjeto";
import SeletorProjetosCliente from "../../ProjetosApuracao/components/SeletorProjetosCliente";
import { lerParametrosProjeto, montarUrlApuracao } from "../../ProjetosApuracao/utils/url.util";
import { selecaoIcmsSt } from "../../ProjetosApuracao/utils/selecao.util";
import {
  usePermissoes,
  CHAVES_APURACOES_EDIT,
  CHAVES_APURACOES_EXPORT,
} from "../../../config/Context/PermissoesContext";
import { sumarizacaoEmAndamento, fmtMesAno, formatarCnpj } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import { formatarQuantidadeLancamentos } from "../Detalhe/utils/formatarQuantidade.util";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";

const NUM_TH = { textAlign: "right" };
const NUM_TD = { textAlign: "right", fontFamily: "ui-monospace, monospace" };
const SEM_BASE_LEGAL = { value: null, label: "Selecione" };

function competenciaYm(competencia) {
  return String(competencia || "").slice(0, 7);
}

/**
 * TXM-473 — "Correlação" e "Fator de conversão" do card, derivados do dado real do motor
 * icmsstapuracao (a linha é um agrupamento, então o critério é "tem alguma ocorrência"):
 *  - correlação: qtd_sem_entrada > 0 → SEM_ENTRADA (saída sem entrada casada); senão CRUZADA.
 *  - fator de conversão: qtd_com_problema > 0 → média ponderada aplicada (mais de uma entrada
 *    cobrindo a mesma saída), que é o "problema de conversão" sinalizado no card.
 */
const CORRELACAO_OPCOES = [
  { value: "", label: "Todas" },
  { value: "cruzada", label: "Cruzadas" },
  { value: "sem_entrada", label: "Sem entrada" },
];

const FATOR_CONVERSAO_OPCOES = [
  { value: "", label: "Todos" },
  { value: "com_problema", label: "Com problema" },
  { value: "sem_problema", label: "Sem problema" },
];

function ehSemEntrada(linha) {
  return (Number(linha.qtdSemEntrada) || 0) > 0;
}

function temProblemaConversao(linha) {
  return (Number(linha.qtdComProblema) || 0) > 0;
}

/**
 * TXM-473 — "credito" vem com sinal do motor icmsstapuracao (base_entrada média maior que
 * base_saida média × alíquota ponderada = crédito recuperável; quando negativo, é imposto a
 * pagar/complementar). Os KPIs do card (Crédito recuperável / Imposto a pagar) separam por
 * sinal em vez de mostrar o líquido, que esconderia a composição.
 */
function calcularTotais(linhas) {
  return linhas.reduce(
    (acc, linha) => {
      // TXM-558: o motor grava recuperável e complemento antes de netar o `credito`; apuração ainda
      // não reprocessada (colunas nulas) cai no líquido por sinal.
      if (linha.creditoRecuperavel != null && linha.creditoComplemento != null) {
        acc.totalCreditoRecuperavel += linha.creditoRecuperavel;
        acc.totalImpostoPagar += linha.creditoComplemento;
      } else {
        const credito = Number(linha.credito) || 0;
        if (credito > 0) acc.totalCreditoRecuperavel += credito;
        else acc.totalImpostoPagar += -credito;
      }
      acc.totalSaidasSemEntrada += Number(linha.qtdSemEntrada) || 0;
      acc.totalConversaoComProblema += Number(linha.qtdComProblema) || 0;
      return acc;
    },
    {
      totalCreditoRecuperavel: 0,
      totalImpostoPagar: 0,
      totalSaidasSemEntrada: 0,
      totalConversaoComProblema: 0,
    }
  );
}

const BASE_LEGAL_SEM = "sem";

/** "mm/aaaa – mm/aaaa" das competências do resultado filtrado, para o modal de Salvar Projeto. */
function periodoDoFiltro(linhas) {
  const meses = linhas.map((linha) => competenciaYm(linha.competencia)).filter(Boolean).sort();
  if (meses.length === 0) return "—";
  return `${fmtMesAno(meses[0])} – ${fmtMesAno(meses[meses.length - 1])}`;
}

/** Campos de filtro que o `?pf=` de um projeto pode reaplicar; o que não está aqui é ignorado. */
const CAMPOS_FILTRO = [
  "cnpjFiltro",
  "buscaFiltro",
  "competenciaFiltro",
  "modeloFiltro",
  "correlacaoFiltro",
  "fatorConversaoFiltro",
  "baseLegalFiltro",
];

/**
 * `apuracaoId` é o da apuração FILHA (ICMS_ST_APURACAO): é nela que o projeto é salvo. A página que
 * hospeda a aba é a do pai (ICMS_APURACAO), que é quem tem a tela.
 */
export default function AbaIcmsSt({ apuracaoId, onTotaisChange, bare }) {
  const { pode } = usePermissoes();
  const podeExportar = pode(CHAVES_APURACOES_EXPORT);
  const podeEditar = pode(CHAVES_APURACOES_EDIT);
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [linhas, setLinhas] = useState([]);
  const [cnpj, setCnpj] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [infoSnack, setInfoSnack] = useState({ open: false, msg: "", severity: "info" });
  const [cnpjFiltro, setCnpjFiltro] = useState("");
  const [buscaFiltro, setBuscaFiltro] = useState("");
  const [competenciaFiltro, setCompetenciaFiltro] = useState("");
  const [modeloFiltro, setModeloFiltro] = useState("");
  const [correlacaoFiltro, setCorrelacaoFiltro] = useState("");
  const [fatorConversaoFiltro, setFatorConversaoFiltro] = useState("");
  const [linhasPagina, setLinhasPagina] = useState([]);
  const [cruzamentoSelecionado, setCruzamentoSelecionado] = useState(null);
  const [modalReprocessarAberto, setModalReprocessarAberto] = useState(false);
  const [baseLegalFiltro, setBaseLegalFiltro] = useState("");
  const [basesLegais, setBasesLegais] = useState([]);
  const [baseLegalSalvando, setBaseLegalSalvando] = useState({});
  const [projetosCliente, setProjetosCliente] = useState([]);
  const [modalSalvarProjeto, setModalSalvarProjeto] = useState(false);
  const [salvandoProjeto, setSalvandoProjeto] = useState(false);
  const [carregado, setCarregado] = useState(false);

  // TXM-558 — `?pf=` reaplica os filtros salvos de um projeto (uma vez por navegação) e `?projeto=`
  // abre o modo consulta dos itens utilizados por aquele projeto aprovado.
  const { filtros: filtrosSalvosUrl, idProjeto: projetoFiltro } = useMemo(
    () => lerParametrosProjeto(location.search),
    [location.search]
  );
  const pfBruto = new URLSearchParams(location.search).get("pf");
  const pfNonce = location.state?.pfNonce;
  const ultimoPfAplicado = useRef({ pf: null, nonce: undefined });
  const reqRef = useRef(0);

  const carregar = useCallback(
    async (silencioso = false) => {
      const reqId = ++reqRef.current;
      if (!silencioso) setLoading(true);
      setErro("");
      const res = projetoFiltro
        ? await buscarSumarizacaoIcmsStApuracao(apuracaoId, { projeto: projetoFiltro })
        : await buscarSumarizacaoIcmsStApuracao(apuracaoId);
      if (reqRef.current !== reqId) return;
      if (!res.sucesso) {
        setErro(res.mensagem || "Sumarização indisponível para esta apuração.");
        setLoading(false);
        return;
      }
      setLinhas(res.linhas);
      setCnpj(res.apuracao?.cnpj || "");
      setEmpresa(res.apuracao?.empresa || "");
      setLoading(false);
      setCarregado(true);
    },
    [apuracaoId, projetoFiltro]
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!carregado || !filtrosSalvosUrl) return;
    const anterior = ultimoPfAplicado.current;
    const mudou = anterior.pf !== pfBruto || (pfNonce !== undefined && pfNonce !== anterior.nonce);
    if (!mudou) return;
    ultimoPfAplicado.current = { pf: pfBruto, nonce: pfNonce };
    const setters = {
      cnpjFiltro: setCnpjFiltro,
      buscaFiltro: setBuscaFiltro,
      competenciaFiltro: setCompetenciaFiltro,
      modeloFiltro: setModeloFiltro,
      correlacaoFiltro: setCorrelacaoFiltro,
      fatorConversaoFiltro: setFatorConversaoFiltro,
      baseLegalFiltro: setBaseLegalFiltro,
    };
    CAMPOS_FILTRO.forEach((campo) => {
      if (typeof filtrosSalvosUrl[campo] === "string") setters[campo](filtrosSalvosUrl[campo]);
    });
  }, [carregado, filtrosSalvosUrl, pfBruto, pfNonce]);

  // Catálogo de bases legais do ICMS-ST; falha aqui não bloqueia a aba (a coluna fica sem opções).
  useEffect(() => {
    let ativo = true;
    buscarBasesLegais("ICMS_ST").then((res) => {
      if (ativo) setBasesLegais(res.basesLegais || []);
    });
    return () => {
      ativo = false;
    };
  }, []);

  const carregarProjetosCliente = useCallback(async () => {
    if (!cnpj) return;
    const res = await listarProjetosDoCliente({ cnpj, tipo: "icms" });
    setProjetosCliente(res.sucesso && Array.isArray(res.projetos) ? res.projetos : []);
  }, [cnpj]);

  useEffect(() => {
    carregarProjetosCliente();
  }, [carregarProjetosCliente]);

  const projetosAprovados = useMemo(
    () => projetosCliente.filter((projeto) => projeto.status === "APROVADO"),
    [projetosCliente]
  );
  const projetosEmPrevia = useMemo(
    () => projetosCliente.filter((projeto) => projeto.status === "EM_PREVIA"),
    [projetosCliente]
  );

  /** TXM-418 — apuração ainda é de 1 CNPJ (multi-CNPJ é o TXM-601); filtro equaliza com o
   * protótipo, hoje com uma opção só. */
  const cnpjOpcoes = useMemo(
    () => [
      { value: "", label: "Todos" },
      ...(cnpj ? [{ value: cnpj, label: formatarCnpj(cnpj) }] : []),
    ],
    [cnpj]
  );

  /** "Modelo do documento" do card — dado real do motor (55 = NF-e, 65 = NFC-e,
   * CUPOM = CF-e/SAT). Correlação e Fator de conversão são derivados de qtd_sem_entrada/
   * qtd_com_problema (ver ehSemEntrada/temProblemaConversao) e alimentam também o badge
   * da coluna Validação. */
  const modeloOpcoes = useMemo(() => {
    const modelos = Array.from(new Set(linhas.map((linha) => linha.modeloDocumento).filter(Boolean))).sort();
    return [
      { value: "", label: "Todos" },
      ...modelos.map((m) => ({ value: m, label: m })),
    ];
  }, [linhas]);

  const competenciaOpcoes = useMemo(() => {
    const competencias = Array.from(new Set(linhas.map((linha) => competenciaYm(linha.competencia)))).sort();
    return [
      { value: "", label: "Todas" },
      ...competencias.map((c) => ({ value: c, label: fmtMesAno(c) })),
    ];
  }, [linhas]);

  // Base legal de cada NCM (select da tabela): "Selecione" + catálogo; uma base fora do catálogo
  // mantém a opção para não zerar a seleção do NCM.
  // Largura do campo = a do maior rótulo do catálogo, para a lista aberta ter o mesmo tamanho do campo.
  const larguraBaseLegal = `min(${Math.max(14, ...basesLegais.map((b) => (b.descricao || "").length)) + 7}ch, 420px)`;

  const opcoesBaseLegalLinha = (linha) => [
    SEM_BASE_LEGAL,
    ...(linha.baseLegalId != null && !basesLegais.some((b) => b.id === linha.baseLegalId)
      ? [{ value: linha.baseLegalId, label: linha.baseLegalDescricao || `#${linha.baseLegalId}` }]
      : []),
    ...basesLegais.map((b) => ({ value: b.id, label: b.descricao })),
  ];

  const baseLegalOpcoes = useMemo(
    () => [
      { value: "", label: "Todas" },
      { value: BASE_LEGAL_SEM, label: "Sem base legal" },
      ...basesLegais.map((b) => ({ value: String(b.id), label: b.descricao })),
    ],
    [basesLegais]
  );

  const projetoOpcoes = useMemo(
    () => [
      { value: "", label: "Todos" },
      ...projetosAprovados.map((p) => ({ value: String(p.idProjeto), label: p.codigo })),
    ],
    [projetosAprovados]
  );

  const handleProjetoChange = useCallback(
    (idProjeto) => {
      const params = new URLSearchParams(location.search);
      params.delete("pf");
      if (idProjeto) params.set("projeto", idProjeto);
      else params.delete("projeto");
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    },
    [location.pathname, location.search, navigate]
  );

  const linhasFiltradas = useMemo(() => {
    let base = linhas;
    if (cnpjFiltro && cnpjFiltro !== cnpj) base = [];
    const termo = buscaFiltro.trim().toLowerCase();
    if (termo) {
      base = base.filter(
        (linha) =>
          String(linha.ncm || "").toLowerCase().includes(termo) ||
          String(linha.ean || "").toLowerCase().includes(termo)
      );
    }
    if (competenciaFiltro) base = base.filter((linha) => competenciaYm(linha.competencia) === competenciaFiltro);
    if (baseLegalFiltro === BASE_LEGAL_SEM) base = base.filter((linha) => linha.baseLegalId == null);
    else if (baseLegalFiltro) base = base.filter((linha) => String(linha.baseLegalId) === baseLegalFiltro);
    if (modeloFiltro) base = base.filter((linha) => linha.modeloDocumento === modeloFiltro);
    if (correlacaoFiltro) {
      const querSemEntrada = correlacaoFiltro === "sem_entrada";
      base = base.filter((linha) => ehSemEntrada(linha) === querSemEntrada);
    }
    if (fatorConversaoFiltro) {
      const querProblema = fatorConversaoFiltro === "com_problema";
      base = base.filter((linha) => temProblemaConversao(linha) === querProblema);
    }
    return [...base].sort((a, b) => (a.competencia < b.competencia ? -1 : a.competencia > b.competencia ? 1 : 0));
  }, [linhas, cnpj, cnpjFiltro, buscaFiltro, competenciaFiltro, modeloFiltro, correlacaoFiltro, fatorConversaoFiltro, baseLegalFiltro]);

  const totais = useMemo(() => calcularTotais(linhasFiltradas), [linhasFiltradas]);

  useEffect(() => {
    onTotaisChange?.(totais);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totais]);

  function limparFiltros() {
    setCnpjFiltro("");
    setBuscaFiltro("");
    setCompetenciaFiltro("");
    setModeloFiltro("");
    setCorrelacaoFiltro("");
    setFatorConversaoFiltro("");
    setBaseLegalFiltro("");
  }

  const selecaoProjeto = useMemo(() => selecaoIcmsSt(linhasFiltradas), [linhasFiltradas]);

  async function handleBaseLegalChange(ncm, baseLegalId) {
    setBaseLegalSalvando((prev) => ({ ...prev, [ncm]: true }));
    const res = await atualizarBaseLegalIcmsSt(apuracaoId, { ncm, baseLegalId });
    setBaseLegalSalvando((prev) => ({ ...prev, [ncm]: false }));
    if (!res.sucesso) {
      setInfoSnack({ open: true, msg: res.mensagem || "Não foi possível salvar a base legal.", severity: "error" });
      return;
    }
    await carregar(true);
  }

  async function handleSalvarProjeto({ sobrescreverIdProjeto }) {
    setSalvandoProjeto(true);
    const res = await salvarProjeto({
      idRelatorioApuracao: Number(apuracaoId),
      filtros: {
        cnpjFiltro,
        buscaFiltro,
        competenciaFiltro,
        modeloFiltro,
        correlacaoFiltro,
        fatorConversaoFiltro,
        baseLegalFiltro,
      },
      chaves: selecaoProjeto.chaves,
      sobrescreverIdProjeto,
    });
    setSalvandoProjeto(false);

    if (!res.sucesso) {
      setInfoSnack({ open: true, msg: res.mensagem || "Não foi possível salvar o projeto.", severity: "error" });
      return;
    }

    setModalSalvarProjeto(false);
    const descartados = Array.isArray(res.descartados) ? res.descartados : [];
    const semDetalhe = descartados.some((d) => d.motivo === "SEM_DETALHE_CREDITO");
    const aviso =
      descartados.length > 0
        ? ` ${descartados.length} ${descartados.length === 1 ? "item não entrou" : "itens não entraram"} no projeto (já utilizados, sem base legal ou sem detalhe do crédito).${
            semDetalhe ? " Reprocesse a apuração para calcular o detalhe." : ""
          }`
        : "";
    setInfoSnack({
      open: true,
      severity: descartados.length > 0 ? "warning" : "success",
      msg: `Projeto ${res.codigo} ${res.criado ? "criado" : "atualizado"} com sucesso!${aviso}`,
    });
    carregarProjetosCliente();
  }

  // Enfileira e acompanha o job até o download (antes só enfileirava e o arquivo nunca
  // chegava ao usuário) — ver Detalhe/hooks/useExportacaoAssincrona.js.
  const exportacao = useExportacaoAssincrona({
    apuracaoId,
    chave: "agrupado",
    enfileirar: () =>
      exportarSumarizacaoApuracao(apuracaoId, { tipo: "agrupado", formato: "csv" }),
    onFinalizar: ({ severity, msg }) => setInfoSnack({ open: true, msg, severity }),
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <GridLoader color="#1565c0" size={12} />
      </Box>
    );
  }

  if (erro) {
    const emAndamento = sumarizacaoEmAndamento(erro);
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={emAndamento ? "info" : "error"}>
          {emAndamento
            ? "Sumarização em processamento — os dados aparecerão aqui assim que for concluída."
            : erro}
        </Alert>
      </Box>
    );
  }

  return (
    <>
    <ApuracaoCardShell
      bare={bare}
      conteudoComScrollProprio
      Icone={Inventory2Icon}
      titulo="Produtos por competência"
      tituloSx={{ fontSize: "1.25rem" }}
      acaoHeader={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
            {linhasFiltradas.length} agrupamentos no filtro
          </Typography>
          <SeletorProjetosCliente
            projetos={projetosCliente}
            onSelecionar={(projeto) =>
              navigate(montarUrlApuracao({ ...projeto, tipo: "icms" }), { state: { pfNonce: Date.now() } })
            }
          />
          {podeEditar && !projetoFiltro && (
            <button
              type="button"
              className={`${styles.btnHeader} ${styles.btnHeaderPrimary}`}
              onClick={() => setModalSalvarProjeto(true)}
            >
              Salvar Projeto
            </button>
          )}
          <BotaoExportacaoProgresso
            progresso={exportacao}
            rotulo="Exportar CSV"
            variant="contained"
            size="small"
            disabled={!podeExportar}
            onClick={() => exportacao.iniciar()}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
          />
          {/* TXM-473 — mesma situação da aba ICMS: reprocessamento por aba dependia do motor
              de apuração (TXM-601). Ambos concluídos, botão habilitado. */}
          <Button
            variant="contained"
            size="small"
            onClick={() => setModalReprocessarAberto(true)}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
          >
            Reprocessar
          </Button>
        </Box>
      }
      filtros={
        // Larguras reduzidas (width em vez do padrão 220px de FILTER_FIELD_SX) — 6 filtros +
        // botão não cabiam numa linha só com a largura padrão. Só afeta esta aba (prop opt-in
        // em SelectFiltro/BuscaFiltro), nenhum outro consumidor muda. width:100% aqui é
        // necessário pra esse wrapper ocupar toda a largura da caixa de filtros (.inputs_header
        // não estica sozinho) — sem isso, o flex-grow dos campos só dividia o espaço que este
        // wrapper já tinha, que era só o necessário pro conteúdo (sobrava vazio à direita).
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", rowGap: "12px", width: "100%" }}>
          <SelectFiltro label="CNPJ" value={cnpjFiltro} onChange={setCnpjFiltro} options={cnpjOpcoes} width={160} />
          <BuscaFiltro
            label="NCM ou EAN"
            placeholder="Pesquisar código"
            value={buscaFiltro}
            onChange={setBuscaFiltro}
            width={130}
          />
          <SelectFiltro label="Competência" value={competenciaFiltro} onChange={setCompetenciaFiltro} options={competenciaOpcoes} width={110} />
          <SelectFiltro label="Base legal" value={baseLegalFiltro} onChange={setBaseLegalFiltro} options={baseLegalOpcoes} width={150} />
          <SelectFiltro label="Projeto" value={projetoFiltro ? String(projetoFiltro) : ""} onChange={handleProjetoChange} options={projetoOpcoes} width={110} />
          <SelectFiltro label="Modelo do documento" value={modeloFiltro} onChange={setModeloFiltro} options={modeloOpcoes} width={110} />
          <SelectFiltro label="Correlação" value={correlacaoFiltro} onChange={setCorrelacaoFiltro} options={CORRELACAO_OPCOES} width={130} />
          <SelectFiltro label="Fator de conversão" value={fatorConversaoFiltro} onChange={setFatorConversaoFiltro} options={FATOR_CONVERSAO_OPCOES} width={140} />
          <Button
            variant="outlined"
            size="small"
            onClick={limparFiltros}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
          >
            Limpar filtros
          </Button>
        </div>
      }
      paginacao={
        linhasFiltradas.length > 0 && (
          <Pagination
            paginationKey="aba-icmsst"
            data={linhasFiltradas}
            filteredData={linhasPagina}
            setFilteredData={setLinhasPagina}
          />
        )
      }
    >
      {projetoFiltro && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Consulta dos itens do projeto{" "}
          {projetosAprovados.find((p) => p.idProjeto === projetoFiltro)?.codigo || `#${projetoFiltro}`}. Não é
          possível editar nem salvar projeto neste modo.
        </Alert>
      )}
      {linhasFiltradas.length === 0 ? (
        <p className={styles.emptyMsg}>Nenhum cruzamento encontrado para esta apuração.</p>
      ) : (
        // Sem padding lateral/wrapper extra — a tabela toca as bordas do card, igual à tela
        // de Apurações (ApuracoesListagem .tableSection): com o wrapper sobraria uma faixa em
        // branco antes da borda onde a linha do registro não chegaria até o fim.
        <div style={{ margin: "8px 0 0", overflow: "auto" }}>
        <table className={tableStyles.accordion_table}>
          <thead>
            <tr>
              <th scope="col" className={`${tableStyles.stickyKeyHeader} ${tableStyles.stickyKeyCol1}`}>CNPJ</th>
              <th scope="col" className={`${tableStyles.stickyKeyHeader} ${tableStyles.stickyKeyCol2}`}>Competência</th>
              <th scope="col">NCM</th>
              <th scope="col">EAN</th>
              <th scope="col">Modelo</th>
              <th scope="col" style={NUM_TH}>Cruzamentos</th>
              <th scope="col" style={NUM_TH}>Base entrada</th>
              <th scope="col" style={NUM_TH}>Base saída</th>
              <th scope="col" style={NUM_TH}>Crédito</th>
              <th scope="col" style={NUM_TH}>Recuperável</th>
              <th scope="col" style={NUM_TH}>Complemento</th>
              <th scope="col">Base legal</th>
              <th scope="col">Projeto</th>
              <th scope="col">Validação</th>
              <th scope="col" style={{ textAlign: "center" }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {linhasPagina.map((linha, idx) => (
              <tr key={`${linha.competencia}-${linha.ncm}-${linha.ean}-${linha.modeloDocumento}-${linha.origemEntrada}-${idx}`}>
                <td className={`${tableStyles.stickyKeyCell} ${tableStyles.stickyKeyCol1}`}>{formatarCnpj(cnpj)}</td>
                <td className={`${tableStyles.stickyKeyCell} ${tableStyles.stickyKeyCol2}`}>{fmtMesAno(competenciaYm(linha.competencia))}</td>
                <td>{linha.ncm || "—"}</td>
                <td>{linha.ean || "—"}</td>
                <td>{linha.modeloDocumento || "—"}</td>
                <td style={NUM_TD}>{formatarQuantidadeLancamentos(linha.qtdCruzamentos)}</td>
                <td style={NUM_TD}>{formatarMoedaBRL(linha.baseEntrada)}</td>
                <td style={NUM_TD}>{formatarMoedaBRL(linha.baseSaida)}</td>
                <td style={NUM_TD}>{formatarMoedaBRL(linha.credito)}</td>
                <td style={NUM_TD}>
                  {linha.creditoRecuperavel == null ? "—" : formatarMoedaBRL(linha.creditoRecuperavel)}
                </td>
                <td style={NUM_TD}>
                  {linha.creditoComplemento == null ? "—" : formatarMoedaBRL(linha.creditoComplemento)}
                </td>
                <td>
                  <Autocomplete
                    size="small"
                    disableClearable
                    options={opcoesBaseLegalLinha(linha)}
                    value={opcoesBaseLegalLinha(linha).find((o) => o.value === linha.baseLegalId) ?? SEM_BASE_LEGAL}
                    onChange={(_, opt) => handleBaseLegalChange(linha.ncm, opt.value)}
                    getOptionLabel={(o) => o?.label ?? ""}
                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                    disabled={!podeEditar || Boolean(projetoFiltro) || linha.utilizado || Boolean(baseLegalSalvando[linha.ncm])}
                    PopperComponent={PopperAjustado}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          "aria-label": `Base legal do NCM ${linha.ncm || "vazio"}`,
                          title: linha.baseLegalDescricao || "Sem base legal",
                        }}
                      />
                    )}
                    sx={{ ...FILTER_FIELD_SX, width: larguraBaseLegal, "& .MuiInputBase-input": { fontSize: "0.875rem" } }}
                  />
                </td>
                <td>{linha.projetoCodigo || "—"}</td>
                <td>
                  {ehSemEntrada(linha) ? (
                    <BadgeStatus tom="alerta">Sem entrada</BadgeStatus>
                  ) : (
                    <BadgeStatus tom="ok">Cruzada</BadgeStatus>
                  )}
                  {temProblemaConversao(linha) && (
                    <Box component="span" sx={{ ml: 0.5 }}>
                      <BadgeStatus tom="alerta">Conversão</BadgeStatus>
                    </Box>
                  )}
                </td>
                <td style={{ textAlign: "center" }}>
                  <button
                    type="button"
                    className={styles.btnHeader}
                    onClick={() => setCruzamentoSelecionado(linha)}
                  >
                    Cruzamentos
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </ApuracaoCardShell>

    <Snackbar
      open={infoSnack.open}
      autoHideDuration={4000}
      onClose={() => setInfoSnack((s) => ({ ...s, open: false }))}
    >
      <Alert severity={infoSnack.severity}>{infoSnack.msg}</Alert>
    </Snackbar>

    <ModalSalvarProjeto
      open={modalSalvarProjeto}
      onClose={() => setModalSalvarProjeto(false)}
      empresa={{ razaoSocial: empresa, cnpj }}
      periodoLabel={periodoDoFiltro(linhasFiltradas)}
      selecao={selecaoProjeto.resumo}
      projetosEmPrevia={projetosEmPrevia}
      salvando={salvandoProjeto}
      onConfirmar={handleSalvarProjeto}
    />

    <ModalDetalhesCruzamentoIcmsSt
      open={!!cruzamentoSelecionado}
      onClose={() => setCruzamentoSelecionado(null)}
      linha={cruzamentoSelecionado}
      cnpj={cnpj}
    />

    <ModalReprocessarAba
      open={modalReprocessarAberto}
      onClose={() => setModalReprocessarAberto(false)}
      onSuccess={(msg) => setInfoSnack({ open: true, msg, severity: "success" })}
      onError={(msg) => setInfoSnack({ open: true, msg, severity: "error" })}
      titulo="Reprocessar apuração ICMS-ST"
      descricao="Recalcula os cruzamentos de entrada x saída (ICMS-ST) com os dados fiscais atuais."
      cnpj={cnpj}
      empresa={empresa}
      idApuracao={apuracaoId}
    />
    </>
  );
}
